<section class="custom-modal" role="dialog" aria-modal="true" aria-labelledby="email-settings-modal-title">
  <header class="modal-header">
    <app-display-icon
      class="modal-header-icon"
      [border]="false"
      [colour]="iconColourEnum.primary"
      [icon]="icons.mail">
    </app-display-icon>
    <div class="modal-header-text">
      <h2 id="email-settings-modal-title" class="modal-title">Email Settings</h2>
      @if (selectedStore?.name) {
        <span class="modal-subtitle">{{ selectedStore.name }}</span>
      }
    </div>
    @if (!(isUserEmailSettingsLoading && !hasInitiallyLoaded)) {
      <button type="button" class="icon-button" aria-label="Close" (click)="close()">
        <app-display-icon [border]="false" [colour]="iconColourEnum.primary" [icon]="icons.close"></app-display-icon>
      </button>
    }
  </header>

  @if (isUserEmailSettingsLoading && !hasInitiallyLoaded) {
    <div class="modal-body modal-body-loading">
      <app-shared-loading-g [animation]="animation"></app-shared-loading-g>
    </div>
  } @else {
    <div class="modal-body">
      <app-shared-accordion class="email-settings-accordion">
        @for (userEntry of editableUsers | keyvalue; track userEntry.key) {
          <app-shared-accordion-item
            [isOpen]="openAccordion === userEntry.key"
            (accordionHeaderClicked)="onAccordionChange($event, userEntry.key)">
            <div class="header user-row">
              <span class="user-id">{{ userEntry.key }}</span>
              <app-display-icon
                [border]="false"
                [colour]="iconColourEnum.primary"
                [icon]="openAccordion === userEntry.key ? icons.chevronUp : icons.chevronDown">
              </app-display-icon>
            </div>
            <div class="body user-form">
              @if (settingsForm && openAccordion === userEntry.key) {
                <div [formGroup]="settingsForm">
                  <div class="form-toolbar">
                    <app-shared-button (buttonClicked)="copy()" [colour]="'primary'">
                      <app-display-icon [border]="false" [colour]="iconColourEnum.primary" [icon]="icons.contentCopy"></app-display-icon>
                      Copy
                    </app-shared-button>
                    <app-shared-button (buttonClicked)="paste()" [colour]="'primary'" [disabled]="!copiedValue">
                      <app-display-icon [border]="false" [colour]="iconColourEnum.primary" [icon]="icons.clipboard"></app-display-icon>
                      Paste
                    </app-shared-button>
                  </div>

                  <div class="form-field">
                    <label class="form-label">Email account</label>
                    <app-shared-custom-select
                      (selectionChange)="onAccountChange($event)"
                      [allowMultiple]="false"
                      [allowSelectAll]="false"
                      [autoSubmitOnSelect]="true"
                      [closeOnAutoSubmit]="true"
                      [isDisabled]="saving || !!microsoftMailBusyLabel"
                      [maxSelections]="1"
                      [placeholder]="'Select email account'"
                      [setOptions]="accountOptions()"
                      [showActionButtons]="false"
                      [showChips]="false"
                      [showSearchBar]="false">
                    </app-shared-custom-select>
                  </div>

                  @if (accountChoice === 'microsoft365') {
                    <div class="form-field">
                      <span class="form-label">Connected mailbox</span>
                      <p class="connected-mailbox">{{ mailboxAddress() || 'Not connected' }}</p>
                    </div>
                    <div class="microsoft-mail-actions">
                      <app-shared-button
                        [fill]="true"
                        [disabled]="microsoftActionsDisabled()"
                        (buttonClicked)="connectMicrosoft365()">
                        Connect Microsoft 365
                      </app-shared-button>
                      @if (microsoftConnected()) {
                        <app-shared-button
                          [disabled]="microsoftActionsDisabled()"
                          (buttonClicked)="disconnectMicrosoft365()">
                          Disconnect
                        </app-shared-button>
                      }
                      <app-shared-button
                        [disabled]="microsoftActionsDisabled()"
                        (buttonClicked)="requestMicrosoftAdminConsent()">
                        Ask your Microsoft 365 admin to approve Gallix
                      </app-shared-button>
                    </div>
                    @if (microsoftMailBusyLabel) {
                      <p class="field-hint">{{ microsoftMailBusyLabel }}</p>
                    }
                    @if (!microsoftMailConfigured) {
                      <p class="field-hint">Microsoft 365 is not configured for this environment.</p>
                    }
                  } @else {
                    <h3 class="form-section-title">Email details</h3>
                    <div class="form-field">
                      <label class="form-label">Sender address</label>
                      <input
                        class="form-input"
                        (input)="changeEvt($event, 'email')"
                        [disabled]="saving || !!testing"
                        autocomplete="off"
                        formControlName="email"
                        type="email">
                    </div>
                    <div class="form-field">
                      <label class="form-label">User name</label>
                      <input
                        class="form-input"
                        (input)="changeEvt($event, 'user')"
                        [disabled]="saving || !!testing"
                        autocomplete="off"
                        formControlName="user">
                    </div>
                    <div class="form-field">
                      <label class="form-label">Password</label>
                      <input
                        class="form-input"
                        (input)="changeEvt($event, 'pwd')"
                        [disabled]="saving || !!testing"
                        autocomplete="off"
                        formControlName="pwd"
                        type="password">
                    </div>

                    <h3 class="form-section-title">SMTP</h3>
                    <div formGroupName="smtp">
                      <div class="form-grid">
                        <div class="form-field form-field--half">
                          <label class="form-label">Server</label>
                          <input
                            class="form-input"
                            (input)="changeEvt($event, 'smtp.server')"
                            [disabled]="saving || !!testing"
                            formControlName="server">
                        </div>
                        <div class="form-field form-field--half">
                          <label class="form-label">Port</label>
                          <input
                            class="form-input"
                            (input)="changeEvt($event, 'smtp.port')"
                            [disabled]="saving || !!testing"
                            formControlName="port">
                        </div>
                      </div>
                      <div class="form-field form-field--toggle">
                        <label class="form-label">SSL only</label>
                        <app-switch-ui
                          [ariaLabel]="'SSL Only'"
                          [checked]="settingsForm.get('smtp.sslOnly')?.value"
                          [disabled]="saving || !!testing"
                          [size]="0.6"
                          (checkedChange)="onToggleChange('smtp.sslOnly', $event)">
                        </app-switch-ui>
                      </div>
                    </div>

                    <h3 class="form-section-title">IMAP</h3>
                    <div class="form-field form-field--toggle">
                      <label class="form-label">Enable IMAP</label>
                      <app-switch-ui
                        [ariaLabel]="'Enable Imap'"
                        [checked]="settingsForm.get('enableImap')?.value"
                        [disabled]="saving || !!testing"
                        [size]="0.6"
                        (checkedChange)="onToggleChange('enableImap', $event)">
                      </app-switch-ui>
                    </div>
                    <div formGroupName="imap">
                      <div class="form-grid">
                        <div class="form-field form-field--half">
                          <label class="form-label">Server</label>
                          <input
                            class="form-input"
                            (input)="changeEvt($event, 'imap.server')"
                            [disabled]="saving || !!testing"
                            formControlName="server">
                        </div>
                        <div class="form-field form-field--half">
                          <label class="form-label">Port</label>
                          <input
                            class="form-input"
                            (input)="changeEvt($event, 'imap.port')"
                            [disabled]="saving || !!testing"
                            formControlName="port">
                        </div>
                      </div>
                      <p class="field-hint">
                        Emails will be organized into a new folder named Gallix, with subfolders categorizing them by type.
                      </p>
                      <div class="form-field form-field--toggle">
                        <label class="form-label">SSL encryption</label>
                        <app-switch-ui
                          [ariaLabel]="'SSL Encryption'"
                          [checked]="settingsForm.get('imap.sslOnly')?.value"
                          [disabled]="saving || !!testing || !settingsForm.get('enableImap')?.value"
                          [size]="0.6"
                          (checkedChange)="onToggleChange('imap.sslOnly', $event)">
                        </app-switch-ui>
                      </div>
                    </div>
                  }
                </div>
              } @else if (openAccordion === userEntry.key) {
                <div class="modal-body-loading">
                  <app-shared-loading-g [animation]="animation"></app-shared-loading-g>
                </div>
              }
            </div>
          </app-shared-accordion-item>
        }
      </app-shared-accordion>
    </div>

    @if (openAccordion && settingsForm) {
      <footer class="modal-footer">
        <app-shared-button
          [fill]="true"
          [disabled]="isSaveDisabled()"
          (buttonClicked)="save()">
          Save & Test Email Settings
        </app-shared-button>
      </footer>
    }
  }
</section>
